import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { Upload, Icon, Button, message, Form, Input } from 'antd';
import { Upload as awsUpload } from '@aws-sdk/lib-storage';
import { S3Client, S3 } from '@aws-sdk/client-s3';
import SparkMD5 from 'spark-md5';
import moment from 'moment';

import { setItem, getItem } from '~helpers/storage';
import * as propertyEditAction from '~actions/properties/property-edit';
import { getFileInfo } from '~helpers/gallery';
import { MUTATION_CREATE_ARTICLE_VIDEO } from '../queries';
import useStyles from './style';

const { Dragger } = Upload;
const MAX_SIZE = 200 * 1024 ** 2;

const VideoComponent = ({ value, onChange }) => {
  const { content } = value;

  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);

  const [createArticleVideo] = useMutation(MUTATION_CREATE_ARTICLE_VIDEO, {
    fetchPolicy: 'network-only',
    onCompleted: res => {
      if (res?.createArticleVideo?.video?.id) {
        setLoading(false);
        onChange(value.id, res?.createArticleVideo?.video?.videoHash);
      }
    },
  });

  const getUploadVideoToken = callback => {
    dispatch(propertyEditAction.getUploadVideoToken(callback));
  };

  const checkVideoUploaded = (hash, callback) => {
    dispatch(propertyEditAction.checkVideoUploaded(hash, callback));
  };

  const loadNext = (currentChunk, chunkSize, file, fileReader) => {
    const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
    const start = currentChunk * chunkSize;
    const end = start + chunkSize >= file.size ? file.size : start + chunkSize;

    fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
  };

  const getFileExt = filename => {
    if (filename) {
      return filename.split('.').pop();
    }

    return 'unknown_file_type';
  };

  const handleInputChange = ev => {
    const title = ev.target.value;

    onChange(value.id, value.content, title);
  };

  const createVideo = (video, md5Value) => {
    createArticleVideo({
      variables: {
        input: {
          videoHash: md5Value,
          path: `${md5Value}.${getFileExt(video.file.name)}`,
          fileName: video.file.name,
          size: video.file.size,
        },
      },
    });
  };

  const handleUploadAWS = (chunkSize, chunks, video) => {
    const { accessKeyId, secretAccessKey, sessionToken, region, bucket } =
      getItem('awsUploadToken');

    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    let currentChunk = 0;

    fileReader.onload = e => {
      spark.append(e.target.result);
      currentChunk += 1;

      video.fileRead = false;

      setLoading(true);
      if (currentChunk < chunks) {
        loadNext(currentChunk, chunkSize, video.file, fileReader);
      } else {
        video.filedRead = true;

        const md5Value = spark.end();
        const s3Config = {
          credentials: {
            accessKeyId,
            secretAccessKey,
            sessionToken,
          },
          region,
        };

        checkVideoUploaded(md5Value, async data => {
          if (!data?.videoIsUploaded) {
            try {
              const key = `${md5Value}.${getFileExt(video.file.name)}`;

              const upload = new awsUpload({
                client: new S3(s3Config) || new S3Client(s3Config),
                partSize: 5 * 1024 ** 2,
                queueSize: 2,
                params: {
                  Bucket: bucket,
                  Key: key,
                  Body: video.file,
                },
                leavePartsOnError: true,
              });

              upload.on('httpUploadProgress', progress => {
                let progressPercentage = Math.floor((progress.loaded * 100) / progress.total);

                setProgress(progressPercentage > 99 ? 100 : progressPercentage);
              });

              upload.done().then(() => {
                createVideo(video, md5Value);
              });
            } catch {
              setError(true);
              setLoading(false);
            }
          } else if (data?.videoIsUploaded) {
            createVideo(video, md5Value);
          } else {
            setError(true);
            setLoading(false);
          }
        });
      }
    };

    fileReader.onerror = () => {
      console.warn('oops, something went wrong.');
    };

    loadNext(currentChunk, chunkSize, video.file, fileReader);
  };

  const handleUploadVideo = item => {
    if (!item.file) {
      return false;
    }

    if (!navigator.onLine) {
      return message.error(t('cms.alert.clienterror.network_connect_failed'));
    }

    if (item.size > MAX_SIZE) {
      return message.error('max size exceed');
    }

    const awsUploadToken = getItem('awsUploadToken');

    if (!awsUploadToken || moment().isAfter(awsUploadToken.expiration)) {
      new Promise((resolve, reject) => {
        getUploadVideoToken(data => {
          if (data && data.getUploadVideoToken) {
            setItem('awsUploadToken', data.getUploadVideoToken);
            resolve('success');
          } else {
            reject('error');
          }
        });
      })
        .then(() => handleUploadVideo(item))
        .catch(err => console.log('get token error:', err));
    } else {
      const chunkSize = 2 * 1024 ** 2;
      const chunks = Math.ceil(item.file.size / chunkSize);

      handleUploadAWS(chunkSize, chunks, item);
    }
  };

  const uploadProps = {
    multiple: false,
    accept: '.mp4',
    beforeUpload: async file => {
      const fileInfoItem = await getFileInfo(file);

      handleUploadVideo(fileInfoItem);
    },
  };

  return (
    <div
      className={classNames(classes.videoContainer, {
        [classes.videoContainerError]: error,
      })}
    >
      <Choose>
        <When condition={loading}>
          <div className={classes.videoLoadingWrapper}>
            <Icon type="video-camera" style={{ fontSize: 48, color: '#38B2A6', marginBottom: 8 }} />
            <p className={classes.videoName}>api.token.mp4</p>

            <div className={classes.videoProgress}>
              <div className={classes.videoProgressBar} style={{ width: 207 }}>
                <div className={classes.videoProgressInner} style={{ width: `${progress}%` }} />
              </div>

              <span className={classes.videoProgressNum}>{progress}%</span>
            </div>
          </div>
        </When>
        <When condition={error}>
          <div className={classes.videoErrorWrapper}>
            <Icon type="warning" style={{ fontSize: 48, color: '#F5222D', marginBottom: 20 }} />
            <div className={classes.videoErrorNotice}>
              {t('content.article.upload.error.video')}
            </div>

            <Upload {...uploadProps}>
              <Button type="link" className={classes.videoReUploadBtn}>
                <Icon type="redo" />
                {t('content.article.upload.re_upload')}
              </Button>
            </Upload>
          </div>
        </When>
        <When condition={content}>
          <div className={classes.videoSuccessWrapper}>
            <div className={classes.videoSuccessContent}>
              <Icon
                type="video-camera"
                style={{ fontSize: 48, color: '#38B2A6', marginBottom: 8 }}
              />
              <p className={classes.videoSuccessName}>{content}.mp4</p>
            </div>

            <div className={classes.videoTitleWrapper}>
              <Form layout={'horizontal'}>
                <Form.Item
                  label={
                    <>
                      <span className={classes.titleLabel}>
                        {t('content.article.upload.title.label')}
                      </span>
                      <span className={classes.optionalLabel}>
                        {t('content.article.upload.optional.label')}
                      </span>
                    </>
                  }
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                >
                  <Input value={value.title} onChange={handleInputChange} />
                </Form.Item>
              </Form>
            </div>
          </div>
        </When>
        <Otherwise>
          <div className={classes.videoWrapper}>
            <Dragger {...uploadProps} showUploadList={false}>
              <div className={classes.wrapper}>
                <Icon type="video-camera" style={{ fontSize: 48, color: '#BFBFBF' }} />
                <p className={classes.uploadTitle}>{t('content.article.upload.notice')}</p>
                <p className={classes.uploadSummary}>{t('content.article.upload.summary.video')}</p>
              </div>
            </Dragger>
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

export default VideoComponent;
